import { Component, input, output, computed, inject, booleanAttribute } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PricingDisplayProduct } from '../../models';
import { PricingStoreService } from '../../services/pricing-store.service';
import { ResponsiveService } from '../../services/responsive.service';

@Component({
  selector: 'product-tabs',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './product-tabs.component.html',
  styleUrls: ['./product-tabs.component.scss']
})
export class ProductTabsComponent {
  /** Store singleton used to read products and selection. */
  private readonly store = inject(PricingStoreService);
  private readonly responsive = inject(ResponsiveService);
  
  /** Horizontal tabs when no sidebar (mobile + tablet), vertical when sidebar exists (desktop) */
  private readonly responsiveMobileOrTablet = this.responsive.isMobileOrTablet;

  /** Force the horizontal (top) layout at every width. */
  readonly forceHorizontal = input(false, { transform: booleanAttribute });

  readonly isMobileOrTablet = computed(() => this.forceHorizontal() || this.responsiveMobileOrTablet());

  /** Reactive computed that mirrors products from the store for template binding. */
  readonly productsSig = computed<PricingDisplayProduct[]>(() => {
    const products = this.store.products();
    console.log('[ProductTabsComponent] products not sorted:', products);
    // Sort by priority (ascending) - lower priority numbers come first
    const sortedProducts = [...products].sort((a, b) => {
      const priorityA = a.priority ?? 999;
      const priorityB = b.priority ?? 999;
      return priorityA - priorityB;
    });
    console.log('[ProductTabsComponent] products sorted:', sortedProducts);
    return sortedProducts;
  });

  /** Show pricing info in tabs */
  readonly showPricing = input(false, { transform: booleanAttribute });

  /** Emits when a product is selected */
  readonly onProductSelect = output<PricingDisplayProduct>();

  // Selected product derived directly from the store.
  readonly selectedProduct = computed<PricingDisplayProduct | null>(() => this.store.selectedProduct());

  /** Returns true when products is a non-empty array */
  readonly hasProducts = computed(() => this.productsSig().length > 0);
  readonly currentProductCode = computed(() => this.selectedProduct()?.code);

  readonly plansCount = computed(() => {
    const product = this.selectedProduct();
    return Array.isArray(product?.plans) ? product!.plans.length : 0;
  });

  readonly popularPlan = computed(() => {
    const product = this.selectedProduct();
    if (!product || !Array.isArray(product.plans)) return '';
    return (product.plans.find(p => p.popular)?.name) || '';
  });

  readonly hasPopularPlan = computed(() => {
    const product = this.selectedProduct();
    return Array.isArray(product?.plans) ? product!.plans.some(p => p.popular) : false;
  });

  constructor() {}

  selectProduct(product: PricingDisplayProduct) {
    // Update store and emit for parent coordination
    this.store.selectProductByCode(product.code);
    this.onProductSelect.emit(product);
  }

  /**
   * TrackBy function for optimal @for rendering performance
   */
  trackByProductCode(index: number, product: PricingDisplayProduct): string {
    return product?.code ?? String(index);
  }

  // Helper: compute lowest monthly price per product for tab display
  getProductPriceFrom(product: PricingDisplayProduct): string {
    if (!product?.plans?.length) return '';
    const prices = product.plans
      .filter(p => p.monthlyPrice !== undefined && p.monthlyPrice !== null)
      .map(p => Number(p.monthlyPrice));
    if (!prices.length) return '';
    const lowest = Math.min(...prices);
    const currency = product.plans[0]?.currency || 'USD';
    return `${currency}${lowest.toLocaleString()}`;
  }
}
